// Store for pre-rendered 3D figure thumbnails (data URLs), keyed by
// FigurePreset. The baker fills it; the picker subscribes and shows each <img>
// as it becomes available (emoji is the fallback).
//
// Thumbnails also persist per device. Baking them means loading the avatar's
// part GLBs, and paying that on every page load — for a picker the therapist
// may not even open — is not worth it. `VERSION` is part of the storage key:
// bump it whenever a change to how figures are drawn would make the stored
// pictures a lie.
import { FigurePreset } from "../domain/types";

const VERSION = 2;
// Colon-separated rather than dot-separated on purpose: the i18n guard in
// src/shared/i18n scans every source file for quoted dotted paths beginning
// with a locale namespace and requires them to exist in both bundles. A
// storage key is not a translation key, and colons keep it out of that net.
const NAMESPACE = "constellation:figureThumb:";
const KEY_PREFIX = `${NAMESPACE}v${VERSION}:`;

const cache = new Map<string, string>();
const listeners = new Set<() => void>();

function storageKey(key: FigurePreset): string {
    return `${KEY_PREFIX}${key}`;
}

/** Load whatever a previous visit baked, and drop the keys of older versions so
 *  a superseded set cannot sit in the quota forever. */
function restore(): void {
    try {
        const stale: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k?.startsWith(NAMESPACE)) continue;
            if (k.startsWith(KEY_PREFIX)) {
                const value = localStorage.getItem(k);
                if (value) cache.set(k.slice(KEY_PREFIX.length), value);
            } else {
                stale.push(k);
            }
        }
        for (const k of stale) localStorage.removeItem(k);
    } catch {
        // No storage (private mode, disabled): bake into memory each load.
    }
}
restore();

export function setThumbnail(key: FigurePreset, dataUrl: string): void {
    cache.set(key, dataUrl);
    try {
        localStorage.setItem(storageKey(key), dataUrl);
    } catch {
        // Over quota or unavailable — the in-memory cache still serves this
        // load, and the next one simply bakes again.
    }
    listeners.forEach((l) => l());
}

export function getThumbnail(key: FigurePreset): string | undefined {
    return cache.get(key);
}

export function hasThumbnail(key: FigurePreset): boolean {
    return cache.has(key);
}

export function subscribeThumbnails(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// Stable snapshot for useSyncExternalStore: bump a token whenever the cache
// changes so consumers re-read.
let token = 0;
const bump = () => {
    token += 1;
};
listeners.add(bump);
export function thumbnailsSnapshot(): number {
    return token;
}
